import type { App } from '../../app.js';
import { h, button, toast } from '../dom.js';
import { allTrackDefs, BotDifficulty } from '@voidline/shared';

/**
 * Championship: five series of escalating difficulty. Each series is a sequence of
 * races; points 10/7/5/3/2/1 by placement, stage advances when you finish a race,
 * the series completes when all races are done — winning overall unlocks XP bombs.
 */

export interface ChampionshipSeries {
  id: string;
  name: string;
  blurb: string;
  unlockLevel: number;
  botDifficulty: BotDifficulty;
  botCount: number;
  laps: number;
  /** trackIds; resolved against the registry at build time. */
  races: string[];
  rewardXp: number;
}

export function championshipSeries(): ChampionshipSeries[] {
  const tracks = allTrackDefs();
  const byDifficulty = (d: number) => tracks.filter((t) => t.difficulty === d).map((t) => t.id);
  const d1 = byDifficulty(1);
  const d2 = byDifficulty(2);
  const d3 = byDifficulty(3);
  const d4 = byDifficulty(4);
  const pick = (arr: string[], n: number, fallback: string[]) => {
    const out = [...arr];
    while (out.length < n) out.push(fallback[out.length % Math.max(1, fallback.length)] ?? 'ionport');
    return out.slice(0, n);
  };
  return [
    {
      id: 'rookie',
      name: 'ROOKIE LEAGUE',
      blurb: 'Three easy circuits. Learn the racing line, win your license.',
      unlockLevel: 0,
      botDifficulty: 'cruise',
      botCount: 5,
      laps: 2,
      races: pick(d1, 3, tracks.map((t) => t.id)),
      rewardXp: 150,
    },
    {
      id: 'circuit',
      name: 'CIRCUIT SERIES',
      blurb: 'The professional midfield. Standard rivals, longer races.',
      unlockLevel: 3,
      botDifficulty: 'standard',
      botCount: 6,
      laps: 3,
      races: pick([...d1.slice(1), ...d2], 4, tracks.map((t) => t.id)),
      rewardXp: 260,
    },
    {
      id: 'apex',
      name: 'APEX SERIES',
      blurb: 'Hostile rivals on advanced circuits. Mistakes cost podiums.',
      unlockLevel: 6,
      botDifficulty: 'hostile',
      botCount: 7,
      laps: 3,
      races: pick([...d2, ...d3], 4, tracks.map((t) => t.id)),
      rewardXp: 400,
    },
    {
      id: 'elimination',
      name: 'SUDDEN DEATH CUP',
      blurb: 'Elimination format. Last place is deleted every twenty seconds.',
      unlockLevel: 9,
      botDifficulty: 'hostile',
      botCount: 7,
      laps: 5,
      races: pick([...d2, ...d3], 3, tracks.map((t) => t.id)),
      rewardXp: 450,
    },
    {
      id: 'voidline',
      name: 'VOIDLINE GRAND PRIX',
      blurb: 'Nemesis rivals. Expert circuits. The name on the trophy.',
      unlockLevel: 12,
      botDifficulty: 'nemesis',
      botCount: 7,
      laps: 3,
      races: pick([...d3, ...d4], 5, tracks.map((t) => t.id)),
      rewardXp: 700,
    },
  ];
}

export const CHAMPIONSHIP_POINTS = [10, 7, 5, 3, 2, 1, 0, 0];

export function buildChampionshipScreen(app: App): HTMLElement {
  const level = app.playerLevel();
  const series = championshipSeries();
  const tracks = allTrackDefs();

  const list = h('div', { class: 'col', style: 'gap: 10px;' });
  for (const s of series) {
    const prog = app.progression.championships[s.id] ?? { stage: 0, points: 0, done: false };
    const locked = s.unlockLevel > level;
    const total = s.races.length;
    const status = prog.done
      ? `COMPLETE · ${prog.points} PTS · ${s.rewardXp} XP AWARDED`
      : prog.stage > 0
        ? `RACE ${prog.stage + 1}/${total} · ${prog.points} PTS`
        : `${total} RACES`;

    list.appendChild(
      h(
        'div',
        {
          class: 'panel row',
          style: `justify-content: space-between; gap: 20px; align-items: center; padding: 14px 20px;
                  opacity: ${locked ? 0.5 : 1};`,
        },
        h('div', {},
          h('div', { class: 'h2', style: 'color: var(--text);', text: s.name }),
          h('div', { class: 'subtle', style: 'font-size: 12px; text-transform: none; max-width: 52ch;', text: s.blurb }),
          h('div', { class: 'subtle mono', style: 'font-size: 10px; margin-top: 4px;', text: status }),
          prog.stage > 0 && !prog.done
            ? h('div', {
                class: 'subtle',
                style: 'font-size: 10px; text-transform: none;',
                text: `Next: ${tracks.find((t) => t.id === s.races[prog.stage])?.name ?? s.races[prog.stage]}`,
              })
            : null
        ),
        locked
          ? h('span', { class: 'subtle', text: `LEVEL ${s.unlockLevel}` })
          : button(
              prog.done ? 'RACE AGAIN' : prog.stage > 0 ? 'CONTINUE' : 'BEGIN',
              () => startChampionshipRace(app, s, prog.done ? 0 : prog.stage),
              `${prog.done ? '' : 'primary '}focusable`
            )
      )
    );
  }

  return h(
    'div',
    { class: 'screen screen-dim' },
    h(
      'div',
      { class: 'panel', style: 'width: min(760px, 94vw); max-height: 88vh; overflow-y: auto;' },
      h(
        'div',
        { class: 'row', style: 'justify-content: space-between; margin-bottom: 16px;' },
        h('div', { class: 'h1', text: 'CHAMPIONSHIP' }),
        button('◄ BACK', () => {
          app.audio.play('ui_back');
          app.show('menu');
        }, 'small focusable')
      ),
      list
    )
  );
}

export function startChampionshipRace(app: App, series: ChampionshipSeries, stage: number): void {
  const trackId = series.races[stage];
  if (!trackId) {
    toast('Series complete!', false);
    return;
  }
  if (stage === 0) {
    app.progression.championships[series.id] = { stage: 0, points: 0, done: false };
    app.saveAll();
  }
  app.startSoloRace({
    trackId,
    trackSeed: null,
    mode: series.id === 'elimination' ? 'elimination' : 'race',
    laps: series.laps,
    botCount: series.botCount,
    botDifficulty: series.botDifficulty,
    tag: `champ:${series.id}:${stage}`,
  });
}

/** Called from App after a championship race's results are known. */
export function recordChampionshipResult(app: App, tag: string, placement: number): string | null {
  const [, seriesId, stageStr] = tag.split(':');
  const series = championshipSeries().find((s) => s.id === seriesId);
  if (!series) return null;
  const stage = Number(stageStr);
  const prog = app.progression.championships[seriesId] ?? { stage: 0, points: 0, done: false };
  prog.points += CHAMPIONSHIP_POINTS[Math.max(0, placement - 1)] ?? 0;
  prog.stage = stage + 1;
  let message: string | null = null;
  if (prog.stage >= series.races.length && !prog.done) {
    prog.done = true;
    const maxPts = series.races.length * CHAMPIONSHIP_POINTS[0];
    const won = prog.points >= maxPts * 0.6;
    app.progression.xp += series.rewardXp;
    message = won
      ? `${series.name} CHAMPION — ${prog.points}/${maxPts} pts, +${series.rewardXp} XP`
      : `${series.name} complete — ${prog.points}/${maxPts} pts, +${series.rewardXp} XP`;
  }
  app.progression.championships[seriesId] = prog;
  app.saveAll();
  return message;
}
